// Shared map constants — the single source every surface (client / rider /
// therapist in apps/app, ops map in apps/admin) reads instead of copy-pasted
// values that can drift. Stack amendment July 2026: Mapbox GL (was MapLibre +
// MapTiler) — vault TECHSTACK.md update pending a vault session.

export type LngLat = [number, number];

export interface ServiceArea {
  id: string;
  label: string;
  /** [sw, ne] as [lng, lat] — usable directly as a Mapbox `maxBounds`. */
  bounds: readonly [LngLat, LngLat];
  center: { lat: number; lng: number };
  defaultZoom: number;
}

/**
 * Pilot = Cainta (Rizal province, borders NCR — not part of it). Widening to
 * full NCR + Rizal later is a pointer swap, not a rewrite.
 */
export const SERVICE_AREAS: Record<"pilot_cainta" | "ncr_rizal" | "luzon", ServiceArea> = {
  pilot_cainta: {
    id: "pilot_cainta",
    label: "Cainta pilot",
    // Hub 14.5786, 121.1222 ± ~13 km — covers the 12.5 km service radius.
    bounds: [
      [121.0, 14.46],
      [121.24, 14.7],
    ],
    center: { lat: 14.5786, lng: 121.1222 },
    defaultZoom: 12,
  },
  ncr_rizal: {
    id: "ncr_rizal",
    label: "NCR + Rizal",
    bounds: [
      [120.9, 14.2],
      [121.45, 14.9],
    ],
    center: { lat: 14.58, lng: 121.1 },
    defaultZoom: 10,
  },
  // Hubs now sit outside Cainta (Hub NI Kharie, near Cabanatuan), and the
  // Cainta box stopped the map from panning to them. Luzon is the pan limit
  // only — which addresses are serviceable is still the hub radius, server-side.
  luzon: {
    id: "luzon",
    label: "Luzon",
    // Mindoro/Masbate in the south-west corner up to Batanes in the north.
    bounds: [
      [119.4, 12.3],
      [124.6, 21.2],
    ],
    center: { lat: 14.5786, lng: 121.1222 },
    defaultZoom: 12,
  },
};

export const ACTIVE_SERVICE_AREA: ServiceArea = SERVICE_AREAS.luzon;

// ── Live position channel — REMOVED (0086) ─────────────────────────────
// `LIVE_TRIPS_CHANNEL` / `LIVE_TRIPS_EVENT` named a Supabase Realtime Broadcast
// channel that NOTHING EVER PUBLISHED TO. The single writer was the client
// app's Dev Panel "Live bridge", deleted with the mocks on 2026-08-08; the
// admin ops map subscribed to the empty channel until 0086 pointed it at
// `ops_live_trips_snapshot()` instead.
//
// ⚠ `GPS_TICK_MS` and `BROADCAST_THROTTLE_MS` below belong to that same dead
// subsystem and have no callers either. They are left in place rather than
// removed in the same change; `MARKER_TWEEN_MS` is live and is tuned against a
// poll interval now, not a simulator tick.

// ── Cadence ──────────────────────────────────────────────────────────────────
/** Mock simulator tick (position source). */
export const GPS_TICK_MS = 1000;
/**
 * Broadcast throttle: publish at most every ~3 s. Readers interpolate between
 * fixes, so this stays smooth without burning mobile data/battery on a ping
 * per second (TECHSTACK: Broadcast is ephemeral; no DB write per ping).
 */
export const BROADCAST_THROTTLE_MS = 3000;
/** Marker tween duration — slightly above the sim tick so motion is continuous. */
export const MARKER_TWEEN_MS = 1100;

// ── Base map (consumed by @bednbody/ui applyBrandStyle) ──────────────────────
/**
 * One base map on every surface — client, therapist, rider (Drive included)
 * and admin: Mapbox Standard, brand-coloured through its config (Leon,
 * 2026-10-21, "same style in every map, go all out"). Lit 3D buildings, land
 * use in separate colours; day and night are its light presets, so a theme or
 * time-of-day switch no longer reloads the style. Replaced light-v11 /
 * dark-v11 / Streets and the rider's navigation-day/night styles.
 */
export const MAPBOX_STYLE_STANDARD = "mapbox://styles/mapbox/standard";
